import React, { useState, useCallback, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Tooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from '@/components/ui/tooltip';
import { 
  Copy, 
  Download, 
  Trash2, 
  Check, 
  FileText,
  Type,
  Hash,
  AlignLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  caseOptions, 
  countCharacters, 
  countWords, 
  countLines,
  copyToClipboard, 
  downloadAsText 
} from '@/utils/caseConverters';

export const ConverterTool = () => {
  const [text, setText] = useState('');
  const [activeCase, setActiveCase] = useState(null);
  const [copied, setCopied] = useState(false);
  const textareaRef = useRef(null);

  // Stats calculation
  const stats = useMemo(() => {
    const chars = countCharacters(text);
    return {
      characters: chars.withSpaces,
      charactersNoSpaces: chars.withoutSpaces,
      words: countWords(text),
      lines: countLines(text)
    };
  }, [text]);

  // Handle case conversion - transforms text in place
  const handleConvert = useCallback((option) => {
    if (!text.trim()) {
      toast.error('Please enter some text to convert');
      return;
    }
    
    // Store cursor position before conversion
    const textarea = textareaRef.current;
    const selectionStart = textarea?.selectionStart || 0;
    const selectionEnd = textarea?.selectionEnd || 0;
    
    // Convert the text
    const result = option.fn(text);
    setText(result);
    setActiveCase(option.id);
    
    // Restore cursor position after state update
    setTimeout(() => {
      if (textarea) {
        textarea.focus();
        // Adjust cursor position - try to maintain relative position
        const newSelectionStart = Math.min(selectionStart, result.length);
        const newSelectionEnd = Math.min(selectionEnd, result.length);
        textarea.setSelectionRange(newSelectionStart, newSelectionEnd);
      }
    }, 0);
    
    toast.success(`Converted to ${option.label}`);
  }, [text]);

  // Handle text change
  const handleTextChange = useCallback((e) => {
    setText(e.target.value);
    // Don't reset activeCase on text change to keep the button highlighted
  }, []);

  // Handle copy
  const handleCopy = useCallback(async () => {
    if (!text.trim()) {
      toast.error('No text to copy');
      return;
    }
    
    const success = await copyToClipboard(text);
    if (success) {
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error('Failed to copy text');
    }
  }, [text]);

  // Handle download
  const handleDownload = useCallback(() => {
    if (!text.trim()) {
      toast.error('No text to download');
      return;
    }
    
    downloadAsText(text, 'converted-text.txt');
    toast.success('Text downloaded!');
  }, [text]);

  // Handle clear
  const handleClear = useCallback(() => {
    setText('');
    setActiveCase(null);
    // Focus the textarea after clearing
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
    toast.info('Text cleared');
  }, []);

  return (
    <section id="converter-tool" className="py-16 md:py-24 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Section header with H1 for SEO */}
          <div className="text-center mb-10">
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-3">
              Text Case Converter
            </h1>
            <p className="text-muted-foreground text-lg">
              Type or paste your text below, then click any button to transform it instantly
            </p>
          </div>

          {/* Main converter card */}
          <Card className="shadow-lg border-border/50 overflow-hidden">
            <CardContent className="p-0">
              {/* Single textarea area */}
              <div className="p-6 border-b border-border/50">
                <div className="flex items-center justify-between mb-3">
                  <label htmlFor="text-converter" className="text-sm font-medium text-foreground flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    Your Text
                    {activeCase && (
                      <Badge variant="secondary" className="ml-2 text-xs">
                        {caseOptions.find(o => o.id === activeCase)?.label}
                      </Badge>
                    )}
                  </label>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Hash className="h-3 w-3" />
                      {stats.characters} chars
                    </span>
                    <span className="flex items-center gap-1">
                      <Type className="h-3 w-3" />
                      {stats.words} words
                    </span>
                    <span className="flex items-center gap-1">
                      <AlignLeft className="h-3 w-3" />
                      {stats.lines} lines
                    </span>
                  </div>
                </div>
                <Textarea
                  ref={textareaRef}
                  id="text-converter"
                  value={text}
                  onChange={handleTextChange}
                  placeholder="Type or paste your text here to convert..."
                  className="min-h-[200px] resize-y text-base bg-background border-border focus:border-primary scrollbar-thin font-mono"
                  aria-label="Text input area for case conversion"
                />
              </div>

              {/* Conversion buttons */}
              <div className="p-6 bg-muted/20 border-b border-border/50">
                <p className="text-sm font-medium text-foreground mb-4">Click to transform your text:</p>
                <TooltipProvider delayDuration={300}>
                  <div className="flex flex-wrap gap-2">
                    {caseOptions.map((option) => (
                      <Tooltip key={option.id}>
                        <TooltipTrigger asChild>
                          <Button
                            variant={activeCase === option.id ? 'default' : 'converter'}
                            size="sm"
                            onClick={() => handleConvert(option)}
                            className="text-sm"
                            aria-label={`Convert to ${option.label}`}
                          >
                            {option.label}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          <p className="font-medium">{option.description}</p>
                          <p className="text-xs opacity-80 mt-1">Example: {option.example}</p>
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </div>
                </TooltipProvider>
              </div>

              {/* Action buttons */}
              <div className="p-6 bg-muted/10">
                <div className="flex flex-wrap gap-3">
                  <Button
                    variant="default"
                    onClick={handleCopy}
                    disabled={!text}
                    className="flex-1 sm:flex-none"
                    aria-label="Copy text to clipboard"
                  >
                    {copied ? (
                      <>
                        <Check className="h-4 w-4" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4" />
                        Copy to Clipboard
                      </>
                    )}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleDownload}
                    disabled={!text}
                    className="flex-1 sm:flex-none"
                    aria-label="Download text as file"
                  >
                    <Download className="h-4 w-4" />
                    Download .txt
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleClear}
                    disabled={!text}
                    className="flex-1 sm:flex-none"
                    aria-label="Clear all text"
                  >
                    <Trash2 className="h-4 w-4" />
                    Clear
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ConverterTool;
